import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * React Query hooks for shop operations
 * Provides automatic caching, loading states, and error handling
 */

// Query keys for shops
export const shopKeys = {
  all: ['shops'],
  lists: () => [...shopKeys.all, 'list'],
  list: (filters) => [...shopKeys.lists(), filters],
  details: () => [...shopKeys.all, 'detail'],
  detail: (id) => [...shopKeys.details(), id],
  bySeller: (sellerId) => [...shopKeys.all, 'seller', sellerId],
  byLocation: (location) => [...shopKeys.all, 'location', location],
};

/**
 * Fetch shop data by ID
 */
async function fetchShop(shopId) {
  const shopRef = doc(db, 'shops', shopId);
  const shopSnap = await getDoc(shopRef);
  
  if (!shopSnap.exists()) {
    throw new Error('Shop not found');
  }
  
  return { id: shopSnap.id, ...shopSnap.data() };
}

/**
 * Fetch shop by seller ID
 * First tries to get by document ID, then queries by sellerId field if not found
 */
export async function fetchShopBySeller(sellerId) {
  // First, try to get shop by document ID (in case sellerId is actually a document ID)
  try {
    const shopRef = doc(db, 'shops', sellerId);
    const shopSnap = await getDoc(shopRef);
    
    if (shopSnap.exists()) {
      return { id: shopSnap.id, ...shopSnap.data() };
    }
  } catch (error) {
    console.log('[fetchShopBySeller] Shop not found by document ID, trying query by sellerId field');
  }
  
  // If not found by ID, query by sellerId field
  const shopsRef = collection(db, 'shops');
  const q = query(shopsRef, where('sellerId', '==', sellerId));
  const shopSnap = await getDocs(q);
  
  if (shopSnap.empty) {
    throw new Error('Shop not found');
  }
  
  const shopDoc = shopSnap.docs[0];
  return { id: shopDoc.id, ...shopDoc.data() };
}

/**
 * Fetch shops by location
 */
async function fetchShopsByLocation(location) {
  const shopsRef = collection(db, 'shops');
  const q = query(shopsRef, where('location', '==', location));
  const shopsSnap = await getDocs(q);
  
  return shopsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Hook to fetch a shop by ID
 */
export function useShop(shopId, options = {}) {
  return useQuery({
    queryKey: shopKeys.detail(shopId),
    queryFn: () => fetchShop(shopId),
    enabled: !!shopId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook to fetch a shop by seller ID
 */
export function useShopBySeller(sellerId, options = {}) {
  return useQuery({
    queryKey: shopKeys.bySeller(sellerId),
    queryFn: () => fetchShopBySeller(sellerId),
    enabled: !!sellerId,
    staleTime: 10 * 60 * 1000, // 10 minutes - increased to reduce refetches
    cacheTime: 15 * 60 * 1000, // 15 minutes - increased cache time
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Prevent refetch on component mount if data exists
    ...options,
  });
}

/**
 * Hook to fetch shops by location
 */
export function useShopsByLocation(location, options = {}) {
  return useQuery({
    queryKey: shopKeys.byLocation(location),
    queryFn: () => fetchShopsByLocation(location),
    enabled: !!location,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook to update shop settings
 */
export function useUpdateShop() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ shopId, updates }) => {
      // This would be implemented with actual Firebase update
      const shopRef = doc(db, 'shops', shopId);
      // await updateDoc(shopRef, updates);
      return { id: shopId, ...updates };
    },
    onSuccess: (updatedShop) => {
      // Update the shop in all relevant caches
      queryClient.setQueryData(
        shopKeys.detail(updatedShop.id),
        updatedShop
      );
      
      // Invalidate seller shop query
      if (updatedShop.sellerId) {
        queryClient.invalidateQueries({
          queryKey: shopKeys.bySeller(updatedShop.sellerId),
        });
      }
      
      // Invalidate location queries if location changed
      if (updatedShop.location) {
        queryClient.invalidateQueries({
          queryKey: shopKeys.byLocation(updatedShop.location),
        });
      }
    },
    onError: (error) => {
      console.error('Failed to update shop:', error);
    },
  });
}

/**
 * Hook to create a new shop
 */
export function useCreateShop() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (shopData) => {
      // This would be implemented with actual Firebase add
      // const docRef = await addDoc(collection(db, 'shops'), shopData);
      // return { id: docRef.id, ...shopData };
      return { id: Date.now().toString(), ...shopData };
    },
    onSuccess: (newShop) => {
      // Add the new shop to the cache
      queryClient.setQueryData(
        shopKeys.detail(newShop.id),
        newShop
      );
      
      // Invalidate seller shop query
      if (newShop.sellerId) {
        queryClient.invalidateQueries({
          queryKey: shopKeys.bySeller(newShop.sellerId),
        });
      }
      
      // Invalidate location queries
      if (newShop.location) {
        queryClient.invalidateQueries({
          queryKey: shopKeys.byLocation(newShop.location),
        });
      }
    },
    onError: (error) => {
      console.error('Failed to create shop:', error);
    },
  });
}

/**
 * Hook to delete a shop
 */
export function useDeleteShop() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (shopId) => {
      // This would be implemented with actual Firebase delete
      // await deleteDoc(doc(db, 'shops', shopId));
      return shopId;
    },
    onSuccess: (shopId) => {
      // Remove the shop from all caches
      queryClient.removeQueries({
        queryKey: shopKeys.detail(shopId),
      });
      
      // Invalidate all shop lists
      queryClient.invalidateQueries({
        queryKey: shopKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('Failed to delete shop:', error);
    },
  });
}
